<script setup lang="js">
import {onUnmounted, ref} from "vue";

import {getServiceStatus, getServiceStatusColor, getVmIcon, getVmStatus} from "@/utils/variables";
import {formatSize} from "@/composables/utility";
import TrafficRemainChart from "@/components/Charts/TrafficRemainChart.vue";
import TrafficLineChart from "@/components/Charts/TrafficLineChart.vue";
import Lucide from "@/base-components/Lucide";
import Button from "@/base-components/Button";
import {useNumberFormat} from "@/composables/useNumberFormat.js";
import ReservedTrafficTable from "./ReservedTrafficTable.vue";
import {useFetch} from "@/composables/useFetch.js";
import {uri} from "@/constants/config.js";
import {Tab} from "@/base-components/Headless";

const {iface,isManager} = defineProps({
  iface: {
    type: Object,
    required: true
  },
  isManager: {
    type: Boolean,
    default: false,
  },

})
const emits = defineEmits(['changeIp', 'activeRequest', 'buy-traffic'])
const trafficBilling = iface.IaasAssetBilling.find(x => x.name === 'Traffic')
const bandwidthBilling = iface.IaasAssetBilling.find(x => x.name === 'Bandwidth')
const chartMaxScale = bandwidthBilling? +bandwidthBilling.unit : undefined
const formatNumber = useNumberFormat().formatNumber

const trafficData = ref({rx: [], tx: []})
const trafficTiming = ref('5minutes')
const trafficList = {
  '5minutes': null,
  'hourly': null,
  'daily': null,
  'monthly': null,
}
const isLoadingTrafficLineChart = ref(true)
async function fetchTraffic(showLoading = true) {
  if (showLoading) isLoadingTrafficLineChart.value =true
  const url = isManager ? uri.admin.iaas.interface.view(iface.id) + '/graph' : uri.api.iaas.interface.view(iface.id) + '/graph'
  const {data} = await useFetch(url, {params: {timing: trafficTiming.value}})
  trafficList[trafficTiming.value] = data ?? {rx: [], tx: []}
  isLoadingTrafficLineChart.value =false
}
async function fetchTrafficCached() {
  if (!trafficList[trafficTiming.value]) await fetchTraffic()
  trafficData.value = trafficList[trafficTiming.value]
}
function setTrafficTiming(value) {
  trafficTiming.value = value
  fetchTrafficCached()
}
fetchTrafficCached()
let timer = null
timer = setInterval(() => {
  fetchTraffic(false)
}, 5000)
onUnmounted(() => {
  clearInterval(timer)
})
</script>

<template>
  <div>
    <div class="flex">
      <span class="font-semibold ml-3"> {{ $t("title.ip") }}: </span>
      <span class="ml-2">
        {{ iface.ip || $t("title.pending") }}
      </span>
      <span
        @click="emits('changeIp', iface)"
        :title="$t('title.resetIp')"
        class="inline-block appearance-none border border-transparent dark:text-white shadow-[0px_3px_20px_#0000000b] rounded-md bg-warning text-white font-medium w-full px-0 py-1 mb-1 text-center cursor-pointer sm:w-10 sm:mb-0 sm:mx-2"
      >
        <Lucide
          title="Reset"
          icon="RefreshCcw"
          class="inline stroke-white-500 hover:stroke-white-700 cursor-pointer"
        />
      </span>

      <div
        class="inline-block appearance-none border border-transparent text-slate-700 dark:text-slate-400 shadow-[0px_3px_20px_#0000000b] rounded-md w-full px-0 py-2 mb-2 text-center sm:w-20 sm:mb-0 sm:mx-2"
      >
        <Lucide
          :icon="getVmIcon(iface.buildStatus, iface.powerStatus)[0]"
          :class="getVmIcon(iface.buildStatus, iface.powerStatus)[1]"
        />
        <span class="mr-1">{{
          $t(getVmStatus(iface.buildStatus, iface.powerStatus))
        }}</span>
      </div>
      <div
        class="inline-block appearance-none border border-transparent text-slate-700 dark:text-slate-400 shadow-[0px_3px_20px_#0000000b] rounded-md w-full px-0 py-2 mb-2 text-center sm:w-20 sm:mb-0 sm:mx-2"
      >
        <Lucide
          :icon="iface.status !== -1 ? 'Activity' : 'PackageX'"
          :class="getServiceStatusColor(iface.status)"
        />
        <span class="mr-1">{{ $t(getServiceStatus(iface.status)) }}</span>
      </div>
      <div
        v-if="bandwidthBilling"
        class="inline-block appearance-none border border-transparent text-slate-700 dark:text-slate-400 shadow-[0px_3px_20px_#0000000b] rounded-md w-full px-0 py-2 mb-2 text-center sm:w-40 sm:mb-0 sm:mx-2"
      >
        <div class="flex">
          <Lucide icon="ChartNetwork" />
          <span class="mr-1">{{ $t("title.bandwidth") }}:</span>
          <span class="mr-1">{{
            formatSize(bandwidthBilling.unit, "mb", 0)
          }}</span>
        </div>
      </div>
      <Button
        v-if="iface.status === 10"
        variant="primary"
        @click="emits('activeRequest', iface)"
        class="ml-0 mr-auto"
      >
        <Lucide icon="LucidePictureInPicture" />
        <span class="mr-1">{{ $t("button.active-request") }}</span>
      </Button>
    </div>
    <div class="grid grid-cols-6 col-span-2 gap-6 mt-8" v-if="trafficBilling">
      <div class="col-span-12 sm:col-span-6 2xl:col-span-3 intro-y">
        <div class="p-5 box zoom-in">
          <div class="flex items-center">
            <div class="flex-none grow">
              <div class="text-lg font-medium truncate">
                {{ trafficBilling.name }}
              </div>
              <div class="mt-1 text-slate-500 ml-5">
                <div class="flex place-content-between">
                  <span>ترافیک کل</span>
                  <span>{{ formatSize(trafficBilling.unit, "mb", 0) }}</span>
                </div>
                <div class="flex place-content-between">
                  <span>ترافیک باقی مانده</span>
                  <span>{{ formatSize(trafficBilling.budget, "mb", 0) }}</span>
                </div>
              </div>
            </div>
            <div class="relative flex-none mr-auto">
              <TrafficRemainChart
                :total="trafficBilling.unit"
                :used="trafficBilling.unit - trafficBilling.budget"
                :width="90"
                :height="90"
              />
              <div
                class="absolute top-0 left-0 flex items-center justify-center w-full h-full font-medium"
              >
                {{
                  formatNumber(
                    (trafficBilling.budget * 100) / trafficBilling.unit
                  )
                }}%
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-span-12 sm:col-span-6 2xl:col-span-3 intro-y">
        <div class="border shadow rounded p-5 w-full">
          <h1 class="border-b pb-1">
            <Lucide icon="TimerReset" class="inline mr-1 ml-1" />
            {{ $t("title.reserveTraffic") }}
          </h1>
          <ReservedTrafficTable :traffics="iface.ReserveTraffics" />
          <div class="mt-2">
            <Button variant="primary" @click="emits('buy-traffic', iface)">
              {{ $t("button.buyReserveTraffic") }}
            </Button>
          </div>
        </div>
      </div>
    </div>
    <Tab.Group class="max-h-full xl:overflow-y-auto" :default-index="0">
      <div class="">گراف مصرف</div>
      <div class="top-0 pb-6 xl:sticky">
        <Tab.List
          variant="pills"
          class="p-1 mx-auto mt-5 border border-dashed rounded-md border-slate-300 dark:border-darkmode-300"
        >
          <Tab>
            <Tab.Button
              as="button"
              class="w-full py-1.5 px-2"
              @click="setTrafficTiming('5minutes')"
            >
              یک ساعت گذشته
            </Tab.Button>
          </Tab>
          <Tab>
            <Tab.Button
              as="button"
              class="w-full py-1.5 px-2"
              @click="setTrafficTiming('hourly')"
            >
              ساعتی
            </Tab.Button>
          </Tab>
          <Tab>
            <Tab.Button
              as="button"
              class="w-full py-1.5 px-2"
              @click="setTrafficTiming('daily')"
            >
              روزانه
            </Tab.Button>
          </Tab>
          <Tab>
            <Tab.Button
              as="button"
              class="w-full py-1.5 px-2"
              @click="setTrafficTiming('monthly')"
            >
              ماهانه
            </Tab.Button>
          </Tab> </Tab.List
        >`
      </div>
    </Tab.Group>
    <free-style-shimmer
      height="20rem"
      width="100"
      v-if="isLoadingTrafficLineChart"
    />
    <div
      v-if="!isLoadingTrafficLineChart"
      :class="[
        'relative',
        'before:content-[\'\'] before:block before:absolute before:w-16 before:left-0 before:top-0 before:bottom-0 before:ml-10 before:mb-7 before:bg-gradient-to-r before:from-white before:via-white/80 before:to-transparent before:dark:from-darkmode-600',
        'after:content-[\'\'] after:block after:absolute after:w-16 after:right-0 after:top-0 after:bottom-0 after:mb-7 after:bg-gradient-to-l after:from-white after:via-white/80 after:to-transparent after:dark:from-darkmode-600',
      ]"
    >
      <TrafficLineChart
        :data="trafficData"
        :scale-max="chartMaxScale"
        class="mt-6 -mb-6"
      />
    </div>
    <div class="text-center my-2 drop-shadow">
      <div class="inline-block border-b w-1/3"></div>
    </div>
  </div>
</template>

<style scoped></style>
